/** Copyright (c) 2023-present ORBIS DS Authors. */

import { resetAuthenticationSlice } from '../storeSlices/sliceAuthentication';
import { showNotification } from '../storeSlices/sliceNotification';
import { notificationErrorMessages } from '../common/keys';
import { StoreDispatch } from '..';

import { navigate } from '../../utils/NavigationUtils';
import routesPaths from '../../routes/paths';

type RtkqCatchError = {
	error: string | null;
	message: string | null;
	meta: {
		response: {
			status: number | null;
			url: string | null;
		};
	};
};

// QUESTION the "routesPaths" on client may be different from the API endpoints !??!?!?!?!?
// I think this a bug !
// I think we should use here the "pathnameApi..."" names from the storeCommon!
const { SIGN_IN, FORGOT_PASSWORD, RESET_PASSWORD } = routesPaths;

const {
	INVALID_REQUEST,
	WRONG_USERNAME_OR_PASSWORD,
	NOT_AUTHORIZED,
	FORBIDDEN,
	RESOURCE_NOT_FOUND,
	SERVICE_UNAVAILABLE,
	GATEWAY_TIMEOUT,
	SOMETHING_WENT_WRONG,
} = notificationErrorMessages;

const handleErrorData = (
	responseError: RtkqCatchError | any,
): { status: number | null; responseUrl: string; message: string | null; errorStatus: string } => {
	return {
		status: responseError?.meta?.response?.status
			? responseError.meta.response.status
			: responseError.error.status,
		responseUrl: responseError?.meta?.response?.url ? responseError.meta.response.url : '',
		message: responseError.error.data ? responseError.error?.data?.message : '',
		errorStatus: responseError?.error?.status || '',
	};
};

/**
 * The user will be redirected if the 'url' contains one of the 'urlPaths'.
 * @param responseUrl The EndPoint from where the Error response comes.
 * @param dispatch
 */
const conditionallyNavigate = (dispatch: StoreDispatch, responseUrl: string): void => {
	const urlPaths = [SIGN_IN, FORGOT_PASSWORD, RESET_PASSWORD, '/login'];

	const isUrlContainsOneOfPaths = (): boolean => {
		return !!urlPaths.find((path: string) => responseUrl.includes(path));
	};

	if (isUrlContainsOneOfPaths()) {
		dispatch(resetAuthenticationSlice());

		if (responseUrl.includes('login')) {
			navigate(SIGN_IN);
		}
	}
};

function handleBadRequest(dispatch, data) {
	const { error } = data;
	dispatch(
		showNotification({
			message: error.data?.message ? error.data.message : INVALID_REQUEST,
			severity: 'error',
		}),
	);
}

function handleUnauthorized(dispatch, responseUrl) {
	dispatch(
		showNotification({
			message: responseUrl.includes('login') ? WRONG_USERNAME_OR_PASSWORD : NOT_AUTHORIZED,
			severity: 'error',
		}),
	);
	conditionallyNavigate(dispatch, responseUrl);
}

/**
 * Logs the Response Error and dispatches actions
 * @param dispatch
 * @param responseError The error from catch
 * @param requestName The query(mutation) name
 * @param displayNotification boolean (default true) shows conditionally Snackbar
 */
const handleResponseError = (
	dispatch: StoreDispatch,
	responseError: RtkqCatchError | any,
	requestName: string,
	displayNotification: boolean = true,
): void => {
	console.error(`Request ${requestName} rejected with:`, responseError);

	const { status, responseUrl, message, errorStatus } = handleErrorData(responseError);

	switch (status) {
		// The BE will not handle images with size bigger than 8Mb after Base64 converting on their side.
		// The error response has incorrect and meaningless information.
		case 200:
			if (errorStatus === 'PARSING_ERROR') {
				dispatch(
					showNotification({
						message: 'The source image size is too large.',
						severity: 'error',
					}),
				);

				break;
			}

			break;

		// Bad Request
		case 400:
			handleBadRequest(dispatch, responseError);
			break;

		// Unauthorized
		case 401:
			handleUnauthorized(dispatch, responseUrl);
			break;

		// Forbidden
		case 403:
			dispatch(
				showNotification({
					message: FORBIDDEN,
					severity: 'error',
				}),
			);

			break;

		// Resource not found
		case 404:
			if (displayNotification) {
				dispatch(
					showNotification({
						message: RESOURCE_NOT_FOUND,
						severity: 'error',
					}),
				);
			}

			break;

		// Unprocessable Entity - Validation Error
		case 422:
			dispatch(
				showNotification({
					message: message || RESOURCE_NOT_FOUND,
					severity: 'error',
				}),
			);

			break;

		// Internal Server Error - Server Issue
		case 500:
			if (displayNotification) {
				dispatch(
					showNotification({
						message: GATEWAY_TIMEOUT,
						severity: 'error',
					}),
				);
			}

			break;

		// Service Unavailable
		case 503:
			dispatch(
				showNotification({
					message: SERVICE_UNAVAILABLE,
					severity: 'error',
				}),
			);

			break;

		// Gateway Timeout
		case 504:
			dispatch(
				showNotification({
					message: GATEWAY_TIMEOUT,
					severity: 'warning',
				}),
			);

			break;

		default:
			if (displayNotification) {
				dispatch(
					showNotification({
						message: SOMETHING_WENT_WRONG,
						severity: 'error',
					}),
				);
			}
	}
};

export default handleResponseError;
